import { dehydrate, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { ReactElement } from 'react';
import styled from 'styled-components';

import theme from '../assets/theme';
import { Loader } from '../components/_atoms/_Loader';
import { Button } from '../components/_atoms/Button';
import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import useAuthGuardPrompt from '../hooks/useAuthGuardPrompt';
import useRandomIcebreaker from '../hooks/useIcebreaker';
import { IndexedTemplate } from '../services/backendService/types';
import DiscoverService from '../services/DiscoverService/DiscoverService';
import { getDiscoverLessonUrl } from '../services/DiscoverService/discoverUtils';
import { CenteredContainer } from '../styles/CenteredContainer';
import { detectAndBestEffortSetLanguage } from '../utils/localisationUtils';

const IceBreaker = ({ template }: { template: IndexedTemplate }) => {
  const session = useSession();
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    template.id,
    undefined,
  );
  return (
    <IceBreakerContainer>
      {authGuardComponent}
      <h1 style={{ fontWeight: 'bold' }}>&quot;{template.title}&quot;</h1>
      {session.status !== 'authenticated' && (
        <Button styles={{ marginTop: 20 }} onClick={showAuthGuard}>
          Play now
        </Button>
      )}
      {session.status === 'authenticated' && (
        <Link
          passHref
          href={getDiscoverLessonUrl({
            templateName: template.title,
            templateId: template.id,
          })}
        >
          <a>
            <Button styles={{ marginTop: 20 }}>Play now</Button>
          </a>
        </Link>
      )}
    </IceBreakerContainer>
  );
};

const IceBreakerContainer = styled.div`
  border-radius: 10px;
  background: white;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const DailyIcebreakerPage: NextPageWithLayout = () => {
  const { data, fetchNew, isLoading } = useRandomIcebreaker();

  return (
    <>
      <Head>
        <title>Daily icebreaker questions</title>
        <meta
          property="og:title"
          content="Get a free daily icebreaker question"
          key="title"
        />
        <meta
          property="description"
          content="Free ready to play ice breaker questions for your team"
          key="description"
        />
        <meta
          name="description"
          content="Free ready to play ice breaker questions for your team"
          key="namedescription"
        />
      </Head>

      <PageContainer>
        <CenteredContainer style={{ flexDirection: 'column' }}>
          {data && <IceBreaker template={data[0]} />}
          {isLoading && (
            <div>
              <Loader />
            </div>
          )}
          <FeelingLuckyButton onClick={fetchNew}>
            Can i get another one?
          </FeelingLuckyButton>
        </CenteredContainer>
      </PageContainer>
    </>
  );
};

const FeelingLuckyButton = styled.p`
  font-weight: bold;
  text-align: center;
  margin-top: 100px;
  cursor: pointer;
`;

DailyIcebreakerPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <LandingPageLayout
      title="Daily icebreaker questions"
      subTitle="Every day, a new ice breaker for your team"
      primaryActionText="Sign up - It's free"
      hideActions
    >
      {page}
    </LandingPageLayout>
  );
};

const PageContainer = styled.div`
  background-color: ${theme.colors.background};
  height: inherit;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-top: 100px;
  padding-bottom: 100px;
`;

export const getServerSideProps: GetServerSideProps = async context => {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(
    ['daily-icebreaker'],
    async () => DiscoverService.getDailyIceBreaker(),
    { staleTime: 1000 * 60 * 5 },
  );
  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
    },
  };
};

export default DailyIcebreakerPage;
